import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ReactNode } from 'react';

dayjs.extend(utc);

export const CHART_WIDTH = 720;
export const PLOT_HEIGHT = 120;
export const GUTTER_LEFT = 28;
export const GUTTER_RIGHT = 8;
const AXIS_HEIGHT = 18;
/** Roughly what "HH:mm" spans at 10px, used to keep labels inside the svg */
const LABEL_WIDTH = 30;
const MUTED_TEXT = 'fill-onlight-text-3 dark:fill-ondark-text-3';
const RULE = 'stroke-onlight-line-2 dark:stroke-ondark-line-2';

/** Width of one bucket's column across the plot. */
export function bucketSlot(buckets: number): number {
  const plotWidth = CHART_WIDTH - GUTTER_LEFT - GUTTER_RIGHT;
  return buckets > 0 ? plotWidth / buckets : plotWidth;
}

/** The y coordinate of a value, with the plot's top a little below yMax. */
export function plotY(value: number, yMax: number): number {
  return PLOT_HEIGHT - (value / yMax) * (PLOT_HEIGHT - 6);
}

/**
 * Bucket times arrive as naive ISO strings in UTC (no offset), so parse
 * them as UTC explicitly -- dayjs(iso) would read them as local -- and show
 * them in the viewer's own timezone.
 */
export function bucketTime(iso: string): string {
  return dayjs.utc(iso).local().format('HH:mm');
}

interface Props {
  ariaLabel: string;
  bucketStarts: string[];
  yMax: number;
  children: ReactNode;
}

/** The svg, y grid and time axis a per-bucket chart draws its marks inside. */
export function BucketChartFrame({
  ariaLabel,
  bucketStarts,
  yMax,
  children,
}: Props) {
  const slot = bucketSlot(bucketStarts.length);
  // One label per ~90px so they never collide.
  const labelEvery = Math.max(1, Math.ceil(90 / slot));
  const right = CHART_WIDTH - GUTTER_RIGHT;

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${PLOT_HEIGHT + AXIS_HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label={ariaLabel}
    >
      {[0.5, 1].map((f) => (
        <line
          key={f}
          x1={GUTTER_LEFT}
          x2={right}
          y1={plotY(yMax * f, yMax)}
          y2={plotY(yMax * f, yMax)}
          className={RULE}
          strokeWidth={1}
        />
      ))}
      <text
        x={GUTTER_LEFT - 6}
        y={plotY(yMax, yMax) + 4}
        textAnchor="end"
        className={MUTED_TEXT}
        fontSize={10}
      >
        {yMax}
      </text>
      <text
        x={GUTTER_LEFT - 6}
        y={PLOT_HEIGHT}
        textAnchor="end"
        className={MUTED_TEXT}
        fontSize={10}
      >
        0
      </text>

      {children}

      <line
        x1={GUTTER_LEFT}
        x2={right}
        y1={PLOT_HEIGHT + 0.5}
        y2={PLOT_HEIGHT + 0.5}
        className={RULE}
      />
      {bucketStarts.map((start, i) => {
        if (i % labelEvery !== 0) return null;
        const x = GUTTER_LEFT + i * slot;
        // The svg clips at its width, so a label that would run past the
        // right edge is drawn ending at its bucket instead of starting there.
        const overflows = x + LABEL_WIDTH > CHART_WIDTH;
        return (
          <text
            key={start}
            x={x}
            y={PLOT_HEIGHT + 13}
            textAnchor={overflows ? 'end' : 'start'}
            className={MUTED_TEXT}
            fontSize={10}
          >
            {bucketTime(start)}
          </text>
        );
      })}
    </svg>
  );
}
